<template>
    <div id="consumption-report-widget-component">
        <widget-wrapper-component
            :title="$t('consumption_report_widget_component.consumption_report')"
            id-widget="consumption-report-widget"
        >
            <template v-slot:default>
                <template v-if="metrics_to_display.length > 0">
                    <DateRangePickerComponent :date_range="date_range" :loading="fetching_consumption" :min_date="min_date" v-on:update="updateRangeDate" />

                    <p class="font-12 mb-0 font-color-medium">
                        {{ $t('consumption_report_widget_component.interval_in_days') }}:
                        {{ consumptions[id_metric]?.date_interval ?? '-' }}
                    </p>

                    <template v-if="!fetching_consumption">
                        <v-slide-y-transition hide-on-leave>
                            <div v-if="!refresh_for_new_tab">
                                <template v-if="has_consumption_data">
                                    <v-tabs v-model="current_tab" class="mb-4" @change="handleNewCurrentTab">
                                        <v-tab v-for="(metric, index) in metrics_to_display" :key="index">
                                            <span :class="`${index === current_tab ? 'font-weight-bold primary--text' : ''}`">
                                                {{ metric.label }}
                                            </span>
                                        </v-tab>
                                    </v-tabs>

                                    <div class="d-flex flex-column flex-sm-row gap-3">
                                        <ConsumptionVariationCardComponent
                                            :class="has_autonomy ? 'flex-half' : 'flex-full'"
                                            :consumption="consumptions[id_metric]"
                                            :unit="metrics_to_display[current_tab].unit"
                                        />
                                        <ConsumptionAutonomyCardComponent
                                            v-if="has_autonomy"
                                            class="flex-half"
                                            :consumption="consumptions[id_metric]"
                                        />
                                    </div>
                                </template>
                                <NoDataSvgComponent v-else icon="mdi-dots-circle" />
                            </div>
                        </v-slide-y-transition>
                    </template>
                </template>

                <template v-else>
                    <NoDataSvgComponent icon="mdi-dots-circle" />
                </template>
            </template>
        </widget-wrapper-component>
    </div>
</template>

<script>
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import DateRangePickerComponent from '@/components/global/picker/DateRangePickerComponent.vue'
import ConsumptionVariationCardComponent from '@/components/equipment/generic/widget/ConsumptionVariationCardComponent.vue'
import ConsumptionAutonomyCardComponent from '@/components/equipment/generic/widget/ConsumptionAutonomyCardComponent.vue'
import ModuleRepository from '@/repositories/ModuleRepository'
import DisplayHuman from '@/mixin/displayHuman'
import { nextTick } from 'vue'
import NoDataSvgComponent from '@/components/global/svg/NoDataSvgComponent.vue'

export default {
    name: 'ConsumptionReportWidgetComponent',
    components: {
        NoDataSvgComponent,
        DateRangePickerComponent,
        WidgetWrapperComponent,
        ConsumptionVariationCardComponent,
        ConsumptionAutonomyCardComponent,
    },
    mixins: [DisplayHuman],
    props: {
        asset_module: {
            type: Object,
            required: true,
        },
        device: {
            type: Object,
            required: true,
        },
        asset: {
            type: Object,
            required: true,
        },
        configuration_widget: {
            type: Object,
            required: true,
            default: function () {
                return {
                    calculate_method_version: 1,
                }
            },
        },
    },
    data() {
        return {
            current_tab: 0,

            date_range: {
                startDate: this.moment().clone().startOf('day').subtract(1, 'month').toDate(),
                endDate: this.moment().toDate(),
            },
            fetching_consumption: true,
            refresh_for_new_tab: false,

            is_average: true,
            consumptions: [],
        }
    },
    computed: {
        min_date() {
            return this.device.date_activation_interface ? this.moment(this.device.date_activation_interface).toDate() : null
        },
        total_models() {
            return this.asset_module.configuration_form_asset.total_models || 0
        },
        metrics_to_display() {
            return this.asset_module.default_metrics_configuration.metrics.filter(
                (asset_module) => asset_module.parameters.displayed_in_widget_consumption
            )
        },
        id_metric() {
            return this.asset_module?.default_metrics_configuration.metrics.findIndex(
                (metric) => metric.parameters === this.metrics_to_display[this.current_tab].parameters
            )
        },
        has_autonomy() {
            const c = this.consumptions[this.id_metric]
            return !!(c?.day_before_threshold_0 && c?.day_before_threshold_1 && c?.day_before_threshold_2)
        },
        has_consumption_data() {
            const c = this.consumptions[this.id_metric]
            return !!(c?.consumption || c?.filling)
        },
    },
    methods: {
        updateRangeDate(date_range) {
            this.date_range = date_range
            this.fetchDeviceDataConsumptionByIdDevice()
        },

        fetchDeviceDataConsumptionByIdDevice() {
            this.fetching_consumption = true
            ModuleRepository.postDeviceVariationsByIdDevice(this.asset_module.id_module, this.device.id_device, {
                date_begin: this.setDateForRequest(this.date_range.startDate),
                date_end: this.setDateForRequest(this.date_range.endDate),
                is_average: JSON.stringify(this.is_average),
                calculate_method_version: this.configuration_widget.calculate_method_version,
            })
                .then((success) => {
                    this.consumptions = success.data.data
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.fetching_consumption = false
                })
        },

        handleNewCurrentTab() {
            this.refresh_for_new_tab = true
            nextTick(() => {
                this.refresh_for_new_tab = false
            })
        },
    },
    created() {
        this.fetchDeviceDataConsumptionByIdDevice()
    },
}
</script>

<style scoped>
.flex-half,
.flex-full {
    width: 100%;
}

@media (min-width: 600px) {
    .flex-half {
        width: 50%;
    }
}
</style>
